import { useEffect, useState } from 'react'
import { useRepository } from '@/data/repository-context'
import type { Rate } from '@/data/types'
import { cn } from '@/lib/cn'
import styles from './RateTicker.module.css'

const REFRESH_MS = 20_000

function RateItem({ rate }: { readonly rate: Rate }) {
  const direction = rate.change > 0.0002 ? 'up' : rate.change < -0.0002 ? 'down' : 'flat'
  return (
    <span className={styles.item}>
      <span className={styles.pair}>{rate.pair}</span>
      <span className={styles.value}>
        {rate.value.toLocaleString('en-US', {
          minimumFractionDigits: rate.decimals,
          maximumFractionDigits: rate.decimals,
        })}
      </span>
      <span className={cn(styles.change, rate.pegged ? styles.pegged : styles[direction])}>
        {rate.pegged
          ? 'official'
          : direction === 'flat'
            ? '—'
            : `${rate.change > 0 ? '▲' : '▼'} ${Math.abs(rate.change * 100).toFixed(2)}%`}
      </span>
    </span>
  )
}

/**
 * Live-ish FX strip. The track holds the list twice so the marquee can loop
 * seamlessly at -50%; it pauses on hover and stops entirely for users who ask
 * for reduced motion, becoming a scrollable strip instead.
 */
export function RateTicker({ className }: { readonly className?: string | undefined }) {
  const repository = useRepository()
  const [rates, setRates] = useState<readonly Rate[]>([])

  useEffect(() => {
    let active = true
    const load = () => {
      repository
        .listRates()
        .then((next) => {
          if (active) setRates(next)
        })
        .catch(() => {
          /* the ticker is ambient: a failed refresh keeps the last values */
        })
    }
    load()
    const timer = window.setInterval(load, REFRESH_MS)
    return () => {
      active = false
      window.clearInterval(timer)
    }
  }, [repository])

  if (rates.length === 0) return null

  return (
    <div
      className={cn(styles.ticker, className)}
      role="marquee"
      aria-label="Live exchange rates"
      style={{ '--pb-ticker-duration': `${rates.length * 5}s` } as React.CSSProperties}
    >
      <div className={styles.track}>
        {[...rates, ...rates].map((rate, index) => (
          <RateItem key={`${rate.pair}-${index}`} rate={rate} />
        ))}
      </div>
    </div>
  )
}
